import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Button, TextField, Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import config from "../config";
import { DAPP_ICON, TransactionName } from "../constants";
import ConfirmModal from "./ConfirmPopup";
import {
  ExecuteReadTransaction,
  ExecuteSubmitTransaction,
  formattedErrorMessage,
} from "../utils";
import MyVerticallyCenteredModal from "./MyVerticallyCenteredModal";
import BigNumber from "bignumber.js";

const getErrorMessage = (message) => {
  try {
    // Remove "Error:" prefix if it exists
    if (message.startsWith("Error:")) {
      message = message.replace("Error: ", "");
    }

    // Try to parse as JSON first (for structured errors)
    if (message.startsWith("{")) {
      const parsedError = JSON.parse(message);

      // Check for specific error message and return custom messages
      if (parsedError?.message?.error === "insufficient balance") {
        return "Not Authorized!";
      }

      // Return the error message from the parsed object if available
      return parsedError.message?.error || "Unknown Error";
    }

    // Handle plain text errors
    if (message === "insufficient balance") {
      return "Not Authorized!";
    }

    // If no mapping, return the original message
    return message;
  } catch (err) {
    // If parsing fails, return the original message or a fallback
    return message || "An unexpected error occurred.";
  }
};

const Burn = ({ isConnected }) => {
  const [receiverAddress, setReceiverAddress] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [balance, setBalance] = useState("");
  const [burnError, setBurnError] = useState(""); // State to handle burn error messages
  const [amount, setAmount] = useState(new BigNumber(0));
  const [isEditing, setIsEditing] = useState(false);
  const [confirmPopupOpen, setConfirmPopupOpen] = useState(false);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const transactionParams = [receiverAddress];
        const [res2, errMsg] = await ExecuteReadTransaction(
          config.CHANNEL_NAME,
          config.CHAINCODE_NAME,
          TransactionName.BALANCE,
          transactionParams
        );
        if (res2) {
          const balanceNum = new BigNumber(res2?.response);
          console.log("Balance is:", balanceNum);
          setBalance(balanceNum?.dividedBy(100));
          setAmount(balanceNum?.dividedBy(100));
        } else {
          throw new Error(formattedErrorMessage(errMsg));
        }
      } catch (error) {
        console.error("Error fetching balance:", error);
      }
    };

    if (receiverAddress) {
      fetchBalance();
    }
  }, [receiverAddress]);

  const handleBurn = async () => {
    setConfirmPopupOpen(false);
    setBurnError(""); // Reset error message
    if (isNaN(amount) || new BigNumber(amount).isEqualTo(0)) {
      setBurnError("No amount to burn. Enter Wallet Address as well.");
      return;
    }
    if (new BigNumber(amount).isGreaterThan(balance)) {
      setBurnError(
        "Burn amount cannot exceed available balance: ₹" +
          new BigNumber(balance).toFormat(2)
      );
      return;
    }
    const transactionParams = [
      receiverAddress,
      new BigNumber(amount).multipliedBy(100).toFixed(0),
    ];

    const [res, errMsg] = await ExecuteSubmitTransaction(
      config.CHANNEL_NAME,
      config.CHAINCODE_NAME,
      TransactionName.BURN,
      transactionParams
    );
    if (res) {
      setResponseMessage(res);
      setReceiverAddress("");
      setAmount(new BigNumber(0));
    } else {
      setResponseMessage(
        `Error: ${
          res?.error
            ? formattedErrorMessage(res.error)
            : formattedErrorMessage(errMsg)
        }`
      );
    }
    setModalShow(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        padding: "20px",
        textAlign: "center",
        backgroundColor: "#f2f4f6",
        borderRadius: "10px",
      }}
    >
      <Box
        sx={{
          padding: "20px",
          textAlign: "center",
          borderRadius: "10px",
          width: "100%",
          maxWidth: "600px",
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: "#7A7A7A", marginBottom: "10px" }}
        >
          Burn
        </Typography>
        {isEditing && new BigNumber(balance).isGreaterThan(0) ? (
          <input
            value={amount}
            type="text"
            onChange={(e) => {
              const regex = /^\d{0,20}(\.\d{0,2})?$/;
              const value = e.target.value
                .replace(/[^0-9.]/g, "") // Remove non-numeric characters except "."
                .replace(/(\..*?)\..*/g, "$1") // Allow only one decimal point
                .replace(/^(\d+\.\d{2})\d+$/, "$1") // Limit to 2 decimal places
                .replace(/^0([0-9]+)/, "$1"); // Remove leading zeros
              if (regex.test(value)) {
                setAmount(value);
              }
            }}
            onBlur={() => {
              if (amount === "") {
                setAmount(new BigNumber(0).toFixed());
              } else {
                setAmount(new BigNumber(amount).toFixed());
              }
              setIsEditing(false);
            }}
            autoFocus
            style={{
              fontWeight: "bold",
              fontSize: "32px",
              textAlign: "center",
              marginBottom: "10px",
              color: "#9E9E9E",
              border: "none",
              textOverflow: "ellipsis", // Add ellipsis if content overflows
              fontSize: "clamp(12px, 5vw, 36px)",
              outline: "none",
              appearance: "textfield", // Removes the number input arrows
            }}
          />
        ) : (
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              marginBottom: "10px",
              color: "black",
              cursor: "pointer",
              textOverflow: "ellipsis", // Add ellipsis if content overflows
              fontSize: "clamp(12px, 5vw, 36px)",
            }}
            onClick={() => setIsEditing(true)} // Enter edit mode on click
          >
            ₹{isNaN(amount) ? "0.00" : new BigNumber(amount).toFormat(2)}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "10px",
          }}
        >
          <img
            src={DAPP_ICON}
            alt="CBDC Icon"
            style={{ width: "20px", height: "20px", marginRight: "5px" }}
          />
          <Typography variant="body1" sx={{ color: "#7A7A7A" }}>
            CBDC
          </Typography>
        </Box>
      </Box>

      <TextField
        placeholder="Enter Wallet Address"
        value={receiverAddress}
        onChange={(e) => {
          setBalance(new BigNumber(0));
          setAmount(new BigNumber(0));
          setReceiverAddress(e.target.value.toLowerCase());
        }}
        variant="outlined"
        fullWidth
        sx={{
          backgroundColor: "white",
          marginTop: "20px",
          borderRadius: "8px",
          marginBottom: "20px",
          maxWidth: "600px",
          textTransform: "lowercase",
        }}
      />

      {burnError && (
        <Typography
          variant="body1"
          sx={{ color: "#e74c3c", marginBottom: "10px" }}
        >
          {burnError}
        </Typography>
      )}

      <Button
        variant="contained"
        disabled={
          new BigNumber(amount).isEqualTo(0) ||
          receiverAddress?.length != 40 ||
          !isConnected
        }
        fullWidth
        onClick={() => {
          setConfirmPopupOpen(true);
        }}
        sx={{
          maxWidth: "600px",
          backgroundColor: "#0055FF",
          color: "white",
          height: "40px",
          borderRadius: "8px",
          textTransform: "none",
          marginBottom: "20px",
        }}
      >
        Burn
      </Button>
      <ConfirmModal
        open={confirmPopupOpen}
        setOpen={setConfirmPopupOpen}
        onConfirm={handleBurn}
      />
      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        message={responseMessage}
        getErrorMessage={getErrorMessage}
      />
    </Box>
  );
};

export default Burn;
